import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { documentsApi, BulkUploadResponse } from '@/lib/documents';
import { AppShell } from '@/components/app/AppShell';
import { InlineAlert } from '@/components/app/Modal';
import { Dropdown } from '@/components/ui/Dropdown';

export function UploadPage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [files, setFiles] = useState<File[]>([]);
  const [languageHint, setLanguageHint] = useState<'auto' | 'en' | 'ja'>('auto');
  const [bulkResult, setBulkResult] = useState<BulkUploadResponse | null>(null);

  const single = useMutation({
    mutationFn: () => documentsApi.upload(wsId!, files[0], languageHint),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['documents', wsId] });
      nav(`/app/workspace/${wsId}/docs`);
    },
  });

  const bulk = useMutation({
    mutationFn: () => documentsApi.bulkUpload(wsId!, files, languageHint),
    onSuccess: (r) => {
      setBulkResult(r);
      void qc.invalidateQueries({ queryKey: ['documents', wsId] });
    },
  });

  const submit = () => {
    if (files.length === 0) return;
    setBulkResult(null);
    if (files.length === 1) single.mutate();
    else bulk.mutate();
  };

  const error = single.error || bulk.error;
  const pending = single.isPending || bulk.isPending;

  return (
    <AppShell
      title={t('upload.heading', { defaultValue: 'Add to the knowledge plane.' })}
      description={t('upload.subheading', {
        defaultValue: 'PDFs, images, fax scans, handwritten 議事録, hanko-stamped contracts — all welcome. Up to 50 MB each, 100 per batch.',
      }) as string}
      maxWidth="max-w-4xl"
    >
      <div className="card-paper p-6 space-y-6">
        <label
          htmlFor="files"
          className="block rounded-xl border-2 border-dashed border-ink-200 p-10 text-center hover:border-accent-400 cursor-pointer transition"
        >
          <input
            id="files"
            type="file"
            multiple
            className="hidden"
            accept=".pdf,.txt,.md,.html,.png,.jpg,.jpeg,.tiff,.bmp,.docx,.csv,.zip,.xlsx"
            onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
          />
          <span className="text-sm text-ink-700">
            {files.length === 0
              ? t('upload.cta', { defaultValue: 'Click to choose files or drag-drop here. Pick multiple for bulk ingest.' })
              : files.length === 1
                ? `${files[0].name} · ${(files[0].size / 1024).toFixed(1)} KB`
                : t('upload.cta_bulk', {
                    defaultValue: '{{n}} files selected · {{total}} MB total',
                    n: files.length,
                    total: (files.reduce((s, f) => s + f.size, 0) / (1024 * 1024)).toFixed(1),
                  })}
          </span>
        </label>

        {files.length > 1 && (
          <ul className="max-h-40 overflow-y-auto text-[12px] text-ink-600 space-y-1 border border-ink-100 rounded-lg p-3 bg-paper-50">
            {files.map((f, i) => (
              <li key={i} className="flex items-center justify-between num">
                <span className="truncate">{f.name}</span>
                <span className="text-ink-400 flex-shrink-0 ml-2">{(f.size / 1024).toFixed(1)} KB</span>
              </li>
            ))}
          </ul>
        )}

        <p className="text-[12px] text-ink-500">
          {t('upload.or_connect', { defaultValue: 'Or connect a source to sync automatically:' })}{' '}
          <Link to={`/app/workspace/${wsId}/connectors`} className="text-accent-600 hover:underline">
            {t('upload.connect_source', { defaultValue: 'Drive, M365, Slack, kintone…' })}
          </Link>
        </p>

        <div className="flex items-center gap-4 text-sm">
          <span className="text-ink-600">{t('upload.lang', { defaultValue: 'Language hint' })}:</span>
          <Dropdown<'auto' | 'en' | 'ja'>
            value={languageHint}
            onChange={(next) => setLanguageHint(next)}
            items={[
              { value: 'auto', label: t('upload.lang_auto', { defaultValue: 'Auto-detect' }) as string },
              { value: 'en', label: 'English', leading: <span aria-hidden>🇺🇸</span> },
              { value: 'ja', label: '日本語', leading: <span aria-hidden>🇯🇵</span> },
            ]}
            ariaLabel={t('upload.lang', { defaultValue: 'Language hint' }) as string}
            size="sm"
          />
        </div>

        {error && (
          <InlineAlert tone="error">
            {String((error as Error)?.message || 'Upload failed')}
          </InlineAlert>
        )}

        {bulkResult && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 px-4 py-3 text-[13px]">
            <div className="font-semibold text-emerald-800">
              {t('upload.bulk_done', {
                defaultValue: '{{accepted}}/{{total}} documents queued. {{failed}} failed.',
                accepted: bulkResult.accepted,
                total: bulkResult.total,
                failed: bulkResult.failed,
              })}
            </div>
            {bulkResult.failed > 0 && (
              <ul className="mt-2 text-warn-700 text-[12px] space-y-0.5">
                {bulkResult.results.filter((r) => !r.documentId).slice(0, 10).map((r, i) => (
                  <li key={i} className="num">
                    {r.originalname}: {r.error}
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-4 text-[12px]">
              <Link to={`/app/workspace/${wsId}/docs`} className="text-accent-600 hover:underline">
                {t('upload.see_documents', { defaultValue: 'Open documents →' })}
              </Link>
              <Link to={`/app/workspace/${wsId}/ask`} className="text-accent-600 hover:underline">
                {t('upload.ask_these', { defaultValue: 'Ask about these' })}
              </Link>
              <Link to={`/app/workspace/${wsId}/search`} className="text-accent-600 hover:underline">
                {t('upload.search_these', { defaultValue: 'Search these' })}
              </Link>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <Link to={`/app/workspace/${wsId}/docs`} className="btn-ghost px-4 py-2 text-sm">
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Link>
          <button
            onClick={submit}
            disabled={files.length === 0 || pending}
            className="btn-press px-4 py-2 text-sm disabled:opacity-40"
          >
            {pending
              ? t('upload.uploading', { defaultValue: 'Uploading…' })
              : files.length > 1
                ? t('upload.submit_bulk', { defaultValue: 'Upload {{n}} files', n: files.length })
                : t('upload.submit', { defaultValue: 'Upload + index' })}
          </button>
        </div>
      </div>
    </AppShell>
  );
}
